// --- FINAL FILE: packages/components/src/project/ProjectCard.tsx ---

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from '@dopomogai/ui';
import { motion } from 'framer-motion';
import { Settings, Trash2, GaugeCircle, ListTodo, Layers3 } from 'lucide-react';
import clsx from 'clsx';

import { IconButton, Badge } from '@dopomogai/ui';
import { useManageProject } from '@dopomogai/agent-kit';
import { useUiStore } from '../../../../../../apps/website/src/stores/ui';
import type { AdminProjectListItem, Project } from '@dopomogai/types';

// Define accent colors for visual variety on the dashboard
type ProjectAccentColor = 'sky' | 'emerald' | 'amber' | 'rose' | 'violet' | 'slate';

const accentColorClasses: Record<ProjectAccentColor, { gradient: string; iconBg: string; }> = {
  sky:    { gradient: 'from-sky-500/10',    iconBg: 'bg-sky-100 text-sky-700' },
  emerald:{ gradient: 'from-emerald-500/10', iconBg: 'bg-emerald-100 text-emerald-700' },
  amber:  { gradient: 'from-amber-500/10',   iconBg: 'bg-amber-100 text-amber-700' },
  rose:   { gradient: 'from-rose-500/10',    iconBg: 'bg-rose-100 text-rose-700' },
  violet: { gradient: 'from-violet-500/10',  iconBg: 'bg-violet-100 text-violet-700' },
  slate:  { gradient: 'from-slate-500/10',   iconBg: 'bg-slate-100 text-slate-700' },
};


interface ProjectCardProps {
  project: AdminProjectListItem & { accentColor?: string };
  onDeleteSuccess: () => void;
  index?: number;
}

export const ProjectCard: React.FC<ProjectCardProps> = ({
  project,
  onDeleteSuccess,
  index = 0,
}) => {
  const { t } = useTranslation('admin_projects');
  const navigate = useNavigate();
  const { openProjectEditor } = useUiStore();
  // --- FIX: The `isDeleting` state is now managed by the hook itself. ---
  const { deleteProject, isLoading: isDeleting } = useManageProject();

  const accent = accentColorClasses[project.accentColor as keyof typeof accentColorClasses || 'slate'];
  
  // --- FIX: The delete handler is now async and awaits the result. ---
  const handleDelete = async (e: React.MouseEvent) => { 
    e.stopPropagation();
    if (window.confirm(t('deleteConfirm', { projectName: project.name }))) {
      // Await the promise from the hook
      const success = await deleteProject(project.id); 
      
      // Now, this check is meaningful and only runs after the API call completes
      if (success) { 
        toast.success(t('deleteSuccessToast', { projectName: project.name }));
        onDeleteSuccess(); // Re-fetches the list of projects
      }
      // Note: The useManageProject hook will automatically show an error toast on failure.
    }
  };
  
  const handleSettingsClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    openProjectEditor(project as Project);
  };
  
  const handleCardClick = () => {
    navigate(`/admin/projects/${project.id}`);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.05 }}
      className="relative group h-full cursor-pointer"
      onClick={handleCardClick}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handleCardClick()}
      role="button"
      tabIndex={0}
      aria-label={t('manageButtonAria', { projectName: project.name })}
    >
      <div className="absolute inset-0 bg-black rounded-xl" />
      <div className={clsx(
        'relative bg-white rounded-xl border-2 border-black flex flex-col h-full',
        'transition-transform duration-200 ease-out group-hover:-translate-x-1 group-hover:-translate-y-1 group-active:translate-x-0 group-active:translate-y-0'
      )}>
        <div className={clsx("p-4 border-b-2 border-black flex items-start justify-between gap-4 bg-gradient-to-tr to-transparent", accent.gradient)}>
            <div className="flex items-center gap-3">
                <div className={clsx("p-2 rounded-lg", accent.iconBg)}>
                    <Layers3 size={20} />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-800 leading-tight">{project.name}</h3>
                   <Badge 
                      text={project.status} 
                      accentColor={project.status === 'Active' ? 'green' : 'gray'} 
                      size="sm"
                      className="mt-1"
                    />
                </div>
            </div>
            <div className="flex items-center flex-shrink-0">
                <IconButton icon={<Settings size={16} />} onClick={handleSettingsClick} tooltip={t('editTooltip')} variant="ghost" size="sm" />
                {/* --- FIX: The delete button is now correctly disabled while the API call is in progress. --- */}
                <IconButton icon={<Trash2 size={16} />} onClick={handleDelete} tooltip={t('deleteTooltip')} variant="danger" size="sm" disabled={isDeleting} />
            </div>
        </div>
        <div className="p-5 flex-grow flex flex-col">
            <p className="text-slate-600 text-sm mb-6 flex-grow">
                {project.description || t('noDescription')}
            </p>
            <div className="border-t border-slate-200 pt-4 space-y-3">
                <div className="flex justify-between items-center text-sm">
                    <span className="flex items-center gap-2 text-slate-500">
                        <GaugeCircle size={14} /> {t('activeSprintsLabel')}
                    </span>
                    <span className="font-bold text-slate-700">{project.active_sprints}</span>
                </div>
                 <div className="flex justify-between items-center text-sm">
                    <span className="flex items-center gap-2 text-slate-500">
                        <ListTodo size={14} /> {t('backlogTasksLabel')}
                    </span>
                    <span className="font-bold text-slate-700">{project.backlog_tasks}</span>
                </div>
            </div>
        </div>
      </div>
    </motion.div>
  );
};